/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '~/root'
import { Formik } from 'formik'
import { Select } from '../Select'
import { useEsignature } from '../../hooks/useEsignature'
import { toast } from '~/services/toast'
import Utils from '~/lib/Utils'
import { isValid } from '../addFolderModal/AddFolderModal'
import { File as fileConstants } from '~/constants/file'
import {
  getFileIdByUuid,
  getFsisFilepicker,
  getFolderStructureParams,
  createFolderStructure,
  type FolderStructureParam
} from '~/services/api/files'
import _ from 'lodash'
import Modal from '../Modal'

interface AddFolderStructureModalProps {
  parentId: number
}

interface FormValues {
  folderStructure: number | null
  stringtype: string
  arraytype: Array<{ value: string; label: string }>
}

const AddFolderStructureModal: React.FC<AddFolderStructureModalProps> = ({ parentId }) => {
  const { t } = useTranslation()
  const { hideModal } = useModal()
  const { signAction } = useEsignature()
  const formRef = useRef<any>(null)
  const context = useServiceContext()

  const [templatesFolders, setTemplatesFolders] = useState<any[]>([])
  const [templatesFoldersOptions, setTemplatesFoldersOptions] = useState<Array<{ value: number; label: string }>>([])
  const [dynamicsFields, setDynamicsFields] = useState<FolderStructureParam[]>([])
  const [isSubmitted, setIsSubmitted] = useState(false)

  const initialFormValues: FormValues = {
    folderStructure: null,
    stringtype: '',
    arraytype: []
  }

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await getFileIdByUuid(fileConstants.uuid.FOLDER_STRUCTURE)
        if (data?.id) {
          const folders = await getFsisFilepicker(
            data.id,
            {
              obsolete: false,
              sortBy: 'name',
              sortDesc: false
            },
            {
              onlyFolders: true
            }
          )
          const optionsData = Utils.transformToSelectOptions(folders, 'id', 'fileName')
          setTemplatesFoldersOptions(optionsData)
          setTemplatesFolders(folders)
        }
      } catch (error) {
        console.error('Error loading folder structure templates:', error)
        toast.error(t('BROWSER_ADD_FOLDER_STRUCTURE_LOAD_TEMPLATES_ERROR'))
      }
    }

    loadTemplates()
  }, [t])

  const onTemplateChange = async (id: number) => {
    try {
      const data = await getFolderStructureParams(id)
      const dynamicsFieldsData = data.params.map((item) => ({
        ...item,
        values: [],
        error: false
      }))
      setDynamicsFields(dynamicsFieldsData)
    } catch (error) {
      console.error('Error loading folder structure parameters:', error)
      toast.error(t('BROWSER_ADD_FOLDER_STRUCTURE_LOAD_PARAMS_ERROR'))
    }
  }

  const save = async (values: FormValues) => {
    const dynmcFld = [...dynamicsFields]
    if (dynmcFld[0]?.isArray) {
      dynmcFld[0].values?.push(...values.arraytype.map((item) => item.value))
    } else {
      dynmcFld[0].values?.push(values.stringtype)
    }

    const requestToSend = {
      parentFolderId: parentId,
      folderStructureTemplateId: values.folderStructure!,
      params: dynmcFld
    }

    hideModal()

    const doAddFolderStructureCallback = async (validation?: any) => {
      try {
        await createFolderStructure({
          ...requestToSend,
          esignature: validation
        })
        toast.success(t('BROWSER_ADD_FOLDER_STRUCTURE_SUCCESS_MSG'))
      } catch (error: any) {
        toast.error(
          t('BROWSER_ADD_FOLDER_STRUCTURE_FAIL_MSG', {
            error: error?.message || error?.data?.message
          })
        )
      }
    }

    if (context.service.auth.requireESignatureForFileCommands()) {
      try {
        const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_ADD_NEW_FOLDER_STRUCTURE', {
          keepParentModalsOpen: true
        })
        return doAddFolderStructureCallback(validation)
      } catch (error) {
        console.error('E-signature cancelled or failed:', error)
      }
    } else {
      return doAddFolderStructureCallback()
    }
  }

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    hideModal()
  }

  return (
    <Modal
      show={true}
      handleClose={hideModal}
      title={t('BROWSER_ADD_FOLDER_STRUCTURE_H_TITLE')}
      size="md"
      className="add-folder-wrapper"
      id="modal-add-file"
    >
      <Formik
        initialValues={initialFormValues}
        innerRef={formRef}
        validate={(values) => {
          const errors: any = {}
          if (values.arraytype?.length === 0 && dynamicsFields[0]?.isArray) {
            errors.arraytype = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          if (!isValid(values.stringtype) && !dynamicsFields[0]?.isArray) {
            errors.stringtype = 'FILENAME_CONTAINS_INVALID_CHARACTERS'
          }
          if (!values.stringtype?.length && !dynamicsFields[0]?.isArray) {
            errors.stringtype = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
          }
          return errors
        }}
        onSubmit={save}
      >
        {({ setFieldValue, errors, touched, values }) => (
          <>
            <Modal.Body>
              <div className="form-group flex-container">
                <label>{t('FOLDER_STRUCTURE_TEMPLATE_LABEL')}</label>
                <Select
                  value={values.folderStructure}
                  placeholder={t('SELECT_FOLDER_STRUCTURE_TEMPLATE_PLACEHOLDER')}
                  name="folderStructure"
                  options={templatesFoldersOptions}
                  isClearable={false}
                  onChange={(option: any) => {
                    setFieldValue('folderStructure', option?.value)
                    if (option?.value) {
                      setFieldValue('arraytype', [])
                      onTemplateChange(option.value)
                    }
                  }}
                />
                {errors.folderStructure && (touched?.folderStructure || isSubmitted) && (
                  <div className="error">{t(errors.folderStructure as string)}</div>
                )}
              </div>

              {dynamicsFields.map((item, index) => {
                return (
                  <div key={index}>
                    {item?.isArray && (
                      <div className="form-group flex-container">
                        <label className="fs12-imp">{item?.name}</label>
                        <Select
                          creatable={true}
                          value={values.arraytype}
                          name="arraytype"
                          isClearable={false}
                          multi={true}
                          onChange={(option: any) => {
                            setFieldValue('arraytype', option)
                          }}
                        />
                        {errors.arraytype && (touched?.arraytype || isSubmitted) && (
                          <div className="error">{t(errors.arraytype as string)}</div>
                        )}
                      </div>
                    )}
                    {!item?.isArray && (
                      <div className="form-group md-form-group">
                        <label htmlFor="in-f" className="fs12-imp">
                          {item.name}
                        </label>
                        <input
                          type="text"
                          className="md-input"
                          id="in-f"
                          name="stringtype"
                          autoFocus
                          maxLength={255}
                          value={values.stringtype}
                          onChange={(event) => {
                            const { value } = event.target
                            const dynamicsFieldsData = [...dynamicsFields]
                            dynamicsFieldsData[index].values[0] = value
                            setFieldValue('stringtype', value)
                            setDynamicsFields(dynamicsFieldsData)
                          }}
                        />
                        {errors.stringtype && (touched?.stringtype || isSubmitted) && (
                          <div className="error fs12">{t(errors.stringtype as string)}</div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </Modal.Body>
            <Modal.Footer>
              <div className="text-right">
                <button className="btn btn-chita btn-close" data-action="cancel" onClick={handleClose} type="button">
                  {t('CANCEL')}
                </button>
                <button
                  data-action="confirm"
                  className="btn btn-primary text-capitalize"
                  type="button"
                  onClick={() => {
                    setIsSubmitted(true)
                    formRef.current?.validateForm().then(() => {
                      const errors = formRef.current.errors
                      if (!Object.keys(errors).length) {
                        save(values)
                      }
                    })
                  }}
                >
                  {t('CREATE')}
                </button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default AddFolderStructureModal
